if (!customElements.get('cart-pair-save')) {
  const cartPairSaveCache = new Map();

  class CartPairSave extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.cart-pair-save__grid');
      this.panelSlot = this.querySelector('.cart-pair-save__panel-slot');
      this.addEventListener('click', this.onClick);
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    async load() {
      const productIds = this.dataset.productIds.split(',').filter(Boolean).slice(0, 4);
      const cartHandles = new Set(this.dataset.cartHandles.split(','));
      const max = Number(this.dataset.max);
      const results = await Promise.all(productIds.map((productId) => this.fetchCards(productId)));
      const seen = new Set();
      const cards = results
        .flat()
        .filter((card) => {
          const handle = card.dataset.productHandle;
          if (cartHandles.has(handle) || seen.has(handle)) return false;
          seen.add(handle);
          return true;
        })
        .slice(0, max);

      if (!cards.length || !this.isConnected) return;
      this.grid.replaceChildren(...cards);
      this.hidden = false;
    }

    async fetchCards(productId) {
      if (!cartPairSaveCache.has(productId)) {
        cartPairSaveCache.set(productId, this.requestCards(productId));
      }
      const cards = await cartPairSaveCache.get(productId);
      return cards.map((card) => card.cloneNode(true));
    }

    async requestCards(productId) {
      const url = `${this.dataset.url}?section_id=${this.dataset.sectionId}&product_id=${productId}&limit=3&intent=complementary`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(html.querySelectorAll('.cart-pair-save__card'));
      } catch (error) {
        console.error('Cart pair & save:', error);
        cartPairSaveCache.delete(productId);
        return [];
      }
    }

    onClick = (event) => {
      const toggle = event.target.closest('.cart-pair-save__add--options');
      if (!toggle) return;
      this.togglePanel(toggle.closest('.cart-pair-save__card'));
    };

    togglePanel(card) {
      const wasOpen = this.openCard === card;
      this.closePanel();
      if (wasOpen) return;

      const panel = card.querySelector('.cart-pair-save__panel');
      this.panelSlot.append(panel);
      panel.hidden = false;
      this.closest('cart-drawer-items')?.scrollTo({ top: this.offsetTop + this.offsetHeight, behavior: 'smooth' });
      card.querySelector('.cart-pair-save__add--options').setAttribute('aria-expanded', 'true');
      this.openCard = card;
      this.openPanel = panel;
    }

    closePanel() {
      if (!this.openCard) return;
      this.openPanel.hidden = true;
      this.openCard.append(this.openPanel);
      this.openCard.querySelector('.cart-pair-save__add--options').setAttribute('aria-expanded', 'false');
      this.openCard = null;
      this.openPanel = null;
    }
  }

  class CartPairSaveOptions extends HTMLElement {
    connectedCallback() {
      this.panel = this.closest('.cart-pair-save__panel');
      this.form = this.panel.querySelector('form');
      this.error = this.querySelector('.cart-pair-save__option-error');
      this.retry = this.querySelector('[data-variant-retry]');
      this.variantResolved ??= true;
      this.addEventListener('change', this.onChange);
      this.retry?.addEventListener('click', this.onChange);
      this.panel.addEventListener('submit', this.onSubmit, true);
      if (!this.variantResolved) this.onChange();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.retry?.removeEventListener('click', this.onChange);
      this.panel.removeEventListener('submit', this.onSubmit, true);
      this.request?.abort();
      this.request = null;
    }

    onSubmit = (event) => {
      if (this.variantResolved && !this.form.querySelector('[name="add"]').disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    optionValues(element = this) {
      return Array.from(element.querySelectorAll('input[type="hidden"], input:checked'))
        .map((input) => input.dataset.optionValueId)
        .join(',');
    }

    onChange = async (event) => {
      event?.stopPropagation();
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const form = this.form;
      const optionValues = this.optionValues();
      this.variantResolved = false;
      form.querySelector('[name="add"]').disabled = true;
      form.querySelector('[name="id"]').disabled = true;
      this.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      const url = `${this.dataset.url}?section_id=cart-pair-save-variant&option_values=${optionValues}`;
      try {
        const response = await fetch(url, {
          signal: request.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isCurrentRequest(request, form)) return;
        this.applyVariant(html, optionValues);
        this.variantResolved = true;
      } catch (error) {
        if (!this.isCurrentRequest(request, form)) return;
        if (this.error) this.error.hidden = false;
        console.error('Cart pair & save options:', error);
      } finally {
        if (this.request === request) {
          this.request = null;
          this.removeAttribute('aria-busy');
        }
      }
    };

    isCurrentRequest(request, form) {
      return this.request === request && this.isConnected && form.isConnected && this.panel.querySelector('form') === form;
    }

    applyVariant(html, optionValues) {
      const form = this.form;
      const newForm = html.querySelector('.cart-pair-save__panel form');
      const newOptions = html.querySelector('cart-pair-save-options');
      const variantId = newForm?.querySelector('[name="id"]')?.value;
      const price = this.panel.querySelector('.cart-pair-save__panel-price');
      const newPrice = html.querySelector('.cart-pair-save__panel-price');
      const button = form.querySelector('[name="add"]');
      const newButton = newForm?.querySelector('[name="add"]');
      const buttonText = button.querySelector('.product-form__add-button-text');
      const newButtonText = newButton?.querySelector('.product-form__add-button-text')?.textContent.trim();
      if (
        newForm?.getAttribute('id') !== form.getAttribute('id') || !/^\d+$/.test(variantId) || !price || !newPrice || !buttonText || !newButtonText ||
        !newOptions || this.optionValues(newOptions) !== optionValues
      ) throw new Error('Incomplete variant response');

      const properties = Array.from(newForm.querySelectorAll('input[name^="properties["]'), (input) => input.cloneNode(true));
      form.querySelector('[name="id"]').value = variantId;
      form.querySelectorAll('input[name^="properties["]').forEach((input) => input.remove());
      form.append(...properties);
      price.replaceChildren(...newPrice.childNodes);
      buttonText.textContent = newButtonText;
      button.toggleAttribute('disabled', newButton.hasAttribute('disabled'));
      form.querySelector('[name="id"]').disabled = newButton.hasAttribute('disabled');
    }
  }

  customElements.define('cart-pair-save', CartPairSave);
  customElements.define('cart-pair-save-options', CartPairSaveOptions);
}
